"use client";

import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { useTranslations } from "next-intl";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "../ui/textarea";
import { AnimatedState } from "@/components/flow/AnimatedState";
import IconSpinnerDots from "../icon/spinner/IconSpinnerDots";
import { twMerge } from "tailwind-merge";
import { Check, Home, Search } from "lucide-react";
import { Link } from "@/i18n/routing";
import { AnimatePresence, motion } from "framer-motion";

type Props = {
  type: "listing";
  listingId: string;
  locale: string;
};

const listingOptions = [
  "UNAVAILABLE",
  "INCORRECT",
  "DUPLICATE",
  "SCAM",
  "OTHER",
] as const;

const ListingBugReport: React.FC<Props> = ({ listingId, locale }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState<"form" | "result">("form");
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [form, setForm] = useState<{
    option: string;
    text: string;
  }>({
    option: listingOptions[0],
    text: "",
  });
  const t = useTranslations("bug-report");

  useEffect(() => {
    if (isOpen) setView("form");
    setForm({
      option: listingOptions[0],
      text: "",
    });
  }, [isOpen]);

  const onSubmitHandler = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setIsLoading(true);

    const response = await fetch("/api/listing/report", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        listingId,
        option: form.option,
        text: form.text,
        locale,
      }),
      cache: "no-store",
    });

    setIsLoading(false);

    if (response.ok) {
      setView("result");
    } else {
      setErrorMessage(t("listing.error"));
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button variant="link" className="text-accent">
          {t("listing.button-title")}
        </Button>
      </SheetTrigger>
      <SheetContent>
        <SheetHeader className={twMerge(view === "result" && "sr-only")}>
          <SheetTitle>{t("listing.title")}</SheetTitle>
          <SheetDescription>{t("listing.description")}</SheetDescription>
        </SheetHeader>
        <AnimatePresence initial={false} mode="popLayout">
          {view === "form" ? (
            <form
              onSubmit={onSubmitHandler}
              key="report-form"
              className="flex flex-col gap-4 w-full pt-4"
            >
              <RadioGroup
                value={form.option}
                onValueChange={(option) => {
                  setForm((prev) => ({ ...prev, option }));
                }}
                required
              >
                <Label className="font-medium">
                  {t("listing.options-label")}
                </Label>
                {listingOptions.map((option) => (
                  <div key={option} className="flex items-center space-x-2">
                    <RadioGroupItem value={option} id={option} />
                    <Label htmlFor={option}>
                      {t(`listing.options.${option}`)}
                    </Label>
                  </div>
                ))}
              </RadioGroup>

              <div className="space-y-2">
                <Label className="font-medium">{t("listing.text-label")}</Label>
                <Textarea
                  value={form.text}
                  onChange={(e) => {
                    setForm((prev) => ({ ...prev, text: e.target.value }));
                  }}
                  placeholder={t("listing.text-placeholder")}
                />
              </div>

              {errorMessage && (
                <div className="text-destructive-foreground">
                  {errorMessage}
                </div>
              )}

              <Button type="submit" disabled={isLoading}>
                <AnimatedState>
                  {isLoading ? <IconSpinnerDots /> : t("submit")}
                </AnimatedState>
              </Button>
            </form>
          ) : (
            <motion.div
              initial={{ opacity: 0, x: 100 }}
              animate={{ opacity: 1, x: 0 }}
              key="report-result"
              className="flex flex-col gap-6 w-full text-foreground"
            >
              <div className="flex flex-col gap-2">
                <div className="mx-auto ">
                  <div className="inline-flex size-11 items-center justify-center rounded-xl bg-secondary">
                    <Check className="w-6 h-6" />
                  </div>
                </div>
                <div className="space-y-2">
                  <p className="text-lg font-semibold">{t("result.title")}</p>
                  <p className="text-muted-foreground text-sm">
                    {t("result.description")}
                  </p>
                </div>
              </div>
              <div className="space-y-2">
                <p className="text-base font-semibold">
                  {t("result.next-title")}
                </p>
                <p className="text-muted-foreground text-base">
                  {t("result.next-description")}
                </p>
              </div>

              <div className="flex flex-col gap-3 w-full">
                <Button asChild>
                  <Link href="/search">
                    <Search className="w-4 h-4" />
                    <span>{t("back-search")}</span>
                  </Link>
                </Button>

                <Button asChild variant="outline">
                  <Link href="/">
                    <Home className="w-4 h-4" />
                    <span>{t("go-home")}</span>
                  </Link>
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </SheetContent>
    </Sheet>
  );
};

export default ListingBugReport;
